import IsLoading from "components/common/IsLoading";
import SingleSelect from "components/common/SingleSelect";
import CustomField from "components/form/CustomField";
import { Form, Formik } from "formik";
import { handleSubmitForm } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import React, { useEffect, useRef, memo, useMemo, useCallback } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { addDynamicPrices } from "store/slices/dynamicPricesSlice";
import { getPlansList } from "store/slices/membershipSlice";
import { COUPON_DISCOUNT_TYPE } from "utils/constants";
import { formatOptionsForSelect } from "utils/formatOptions";
import { getDynamicPricesValidationSchema } from "utils/validations";

const DynamicPrices = memo(({ onCancel, onSuccess }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const { canAction } = usePermission();
  const { canView, canEdit } = canAction("freewash_rule");

  // Split selectors - only subscribe to what's needed
  const loading = useSelector((state) => state.Membership.loading);
  const isLoading = useSelector((state) => state.Membership.isLoading);
  const plansList = useSelector((state) => state.Membership.plansList);

  const [initialValues, setInitialValues] = React.useState({
    planId: "",
    maxCustomers: "",
    type: "",
    value: "",
  });
  useEffect(() => {
    dispatch(getPlansList());
  }, [dispatch]);

  // Helper function to get selected plan - will be called inside Formik render
  const getSelectedPlan = useCallback(
    (planId) => {
      if (!planId || !plansList?.length) return null;
      return plansList.find((plan) => plan._id === planId);
    },
    [plansList],
  );

  const handleSubmit = useCallback(
    (values) => {
      const { planId, maxCustomers, type, value } = values;

      const payload = {
        planId,
        maxCustomers: Number(maxCustomers),
        type,
        value: Number(value),
      };

      handleSubmitForm({
        dispatch,
        action: addDynamicPrices,
        payload,
        onSuccess,
      });
    },
    [dispatch, onSuccess],
  );

  if (!canView) return null;
  if (loading) {
    return <IsLoading />;
  }

  return (
    <div className="">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={getDynamicPricesValidationSchema(t)}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue }) => {
          return (
            <Form>
              <Row>
                <>
                  <Col lg={6}>
                    <div className="form_field">
                      <SingleSelect
                        label={t("Plan")}
                        required
                        value={values.planId}
                        options={formatOptionsForSelect(plansList, language)}
                        handleSingleSelectChange={(e) => {
                          const selectedPlanId = e.value;
                          setFieldValue("planId", selectedPlanId);
                        }}
                        error={
                          errors.planId && touched.planId ? errors.planId : null
                        }
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form_field">
                      <SingleSelect
                        required
                        label={t("Type")}
                        name={`type`}
                        options={COUPON_DISCOUNT_TYPE}
                        value={values?.type}
                        handleSingleSelectChange={(opt) =>
                          setFieldValue(`type`, opt.value)
                        }
                        error={errors.type && touched.type ? errors.type : null}
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form_field">
                      <CustomField
                        label={t("Value")}
                        name="value"
                        placeholder={
                          values?.type === "percentage" ? t("10%") : t("10 SAR")
                        }
                        required
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form_field">
                      <CustomField
                        label={t("Maximum Customers")}
                        name="maxCustomers"
                        placeholder={t("Enter maximum customer")}
                        required
                      />
                    </div>
                  </Col>
                </>

                <Col lg={12} className="d-flex justify-content-end gap-2 mt-5">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={onCancel}
                  >
                    {t("Cancel")}
                  </button>

                  <button
                    type="submit"
                    className="main_btn"
                    disabled={isLoading || !canEdit}
                  >
                    {t("Save Changes")}
                  </button>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
});

DynamicPrices.displayName = "DynamicPrices";

export default DynamicPrices;
